/**
 * Learning content static data.
 *
 * Provides static data and helper functions for learning materials including
 * grammar topics structure, sentence categories, and PDF book listings.
 * Includes conversion utilities for transforming grammar topics data structures.
 *
 * @module learningData
 */

import {
  GrammarLevel,
  PDFBook,
  ProficiencyLevel,
} from '@/types/learning';
import { getTopicFilename } from '@/lib/contentLoader';

/**
 * Converts a flat grammar topics structure to GrammarLevel format.
 *
 * Transforms a record of level-to-topics mapping into an array of GrammarLevel
 * objects with proper structure including IDs, metadata, and default values.
 *
 * @param grammarTopics - Record mapping proficiency levels to arrays of topic objects with title and description
 * @returns Array of GrammarLevel objects, one for each proficiency level (A1-C2)
 *
 * @example
 * ```typescript
 * const topics = {
 *   A1: [{ title: "Personalpronomen", description: "..." }],
 *   B1: [{ title: "Dative Case", description: "..." }]
 * };
 * const levels = convertGrammarTopicsToLevels(topics);
 * ```
 */
export function convertGrammarTopicsToLevels(
  grammarTopics: Record<string, Array<{ title: string; description: string }>>,
): GrammarLevel[] {
  const levelNames: Record<ProficiencyLevel, string> = {
    A1: 'Beginner',
    A2: 'Elementary',
    B1: 'Intermediate',
    B2: 'Upper Intermediate',
    C1: 'Advanced',
    C2: 'Proficient',
  };

  const levels: ProficiencyLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

  return levels.map((level) => {
    const topics = grammarTopics[level] || [];
    return {
      level,
      name: levelNames[level],
      topics: topics.map((topic) => ({
        id: getTopicFilename(topic.title),
        title: topic.title,
        description: topic.description,
        level,
        estimatedTime: 10, // Default estimate
        hasLesson: true,
        hasQuiz: true,
        status: 'not-started' as const,
      })),
    };
  });
}

/**
 * Default grammar topics structure organized by proficiency level.
 *
 * Contains comprehensive grammar topics for levels A1-C2 with titles and descriptions.
 * This data structure is used to generate the grammar learning content structure.
 */
export const defaultGrammarTopics: Record<string, Array<{ title: string; description: string }>> = {
  A1: [
    { title: "Personalpronomen", description: "ich, du, er, sie, es, wir, ihr, sie" },
    { title: "Konjugation Präsens", description: "Verbkonjugation im Präsens" },
    { title: "Besondere Verben", description: "sein, haben, möchten, mögen, wissen, tun" },
    { title: "Verben mit Vokalwechsel", description: "e→i, a→ä Verbkonjugationen" },
    { title: "Modalverben", description: "können, müssen, wollen, sollen, dürfen, möchten" },
    { title: "Modalverben Gebrauch", description: "Wann verwendet man können, wollen, möchten" },
    { title: "Modalverben Gebrauch 2", description: "müssen vs. dürfen, sollen für Vorschläge und Verpflichtungen" },
    { title: "Trennbare Verben", description: "aufstehen, einkaufen, anrufen - Präfix-Trennungsregeln" },
    { title: "Imperativ", description: "Befehle, Bitten, Ratschläge - Sie, du, ihr Formen" },
    { title: "W-Fragen", description: "wer, was, wo, wann, wie, warum - Fragewörter und Wortstellung" },
    { title: "Ja-/Nein-Fragen", description: "Verb-Subjekt-Umstellung, ja/nein/doch Antworten" },
    { title: "Wortstellung", description: "Verb Position 2, Inversion, Zeit und Ort" },
    { title: "Nomen Plural", description: "Pluralendungen, Umlaute, unregelmäßige Formen" },
    { title: "Artikel", description: "der/die/das, ein/eine, keine Artikel Regeln" },
    { title: "Negation", description: "nicht vs. kein/keine/keinen - Verneinung richtig verwenden" },
    { title: "Akkusativ", description: "den/einen/keinen - direkte Objekte richtig verwenden" },
    { title: "Dativ", description: "dem/einem/keinem - indirekte Objekte und Präpositionen" },
    { title: "Possessivartikel", description: "mein/dein/sein/ihr/unser/euer - Besitz richtig ausdrücken" },
    { title: "Demonstrativ- und Interrogativpronomen", description: "dieser/welcher - auf Dinge zeigen und Fragen stellen" },
    { title: "Personalpronomen Akkusativ und Dativ", description: "mich/dich/ihn/sie - mir/dir/ihm/ihr richtig verwenden" },
    { title: "Präteritum sein und haben", description: "war/hatte - über die Vergangenheit sprechen" },
    { title: "Perfekt mit haben", description: "habe gemacht/gegessen/getrunken - abgeschlossene Handlungen" },
    { title: "Zeitpräpositionen", description: "im/am/um/von...bis - Zeitangaben richtig verwenden" },
    { title: "Präpositionen mit Dativ", description: "aus/bei/mit/nach/seit/von/zu - immer Dativ verwenden" },
    { title: "Präpositionen mit Akkusativ", description: "für/um/durch/ohne/gegen (FUDOG) - immer Akkusativ verwenden" },
    { title: "Wechselpräpositionen mit Dativ", description: "in/an/auf/vor/hinter/über/unter/neben/zwischen - wo? (Dativ)" },
    { title: "Konjunktionen", description: "und/aber/oder/denn - Hauptsätze verbinden (Position 0)" },
    { title: "Komposita", description: "zusammengesetzte Nomen - Wort 1 + Wort 2 = Wort 3" },
    { title: "Zusammengesetzte Verben", description: "aus/an, zu/auf, da/weg, dafür/dagegen - gesprochene Sprache" },
    { title: "Artikel und Nomen Genus", description: "der/die/das - Genusregeln und Ausnahmen" },
  ],
  A2: [
    { title: "Past Tense (Perfekt)", description: "Perfect tense with haben and sein" },
    { title: "Accusative Case", description: "Direct objects and accusative prepositions" },
    { title: "Comparative & Superlative", description: "Comparing adjectives and adverbs" },
  ],
  B1: [
    { title: "Dative Case", description: "Indirect objects and dative prepositions" },
    { title: "Genitive Case", description: "Possessive case and genitive prepositions" },
    { title: "Subjunctive II", description: "Conditional and hypothetical situations" },
    { title: "Passive Voice", description: "Formation and usage of passive constructions" },
  ],
  B2: [
    { title: "Complex Sentence Structure", description: "Subordinate clauses and conjunctions" },
    { title: "Participle Constructions", description: "Present and past participles" },
    { title: "Reported Speech", description: "Indirect speech and subjunctive I" },
    { title: "Advanced Prepositions", description: "Complex prepositional phrases" },
  ],
  C1: [
    { title: "Advanced Subjunctive", description: "Nuanced use of subjunctive moods" },
    { title: "Stylistic Variations", description: "Formal vs informal language" },
    { title: "Complex Text Analysis", description: "Literary and academic texts" },
    { title: "Advanced Syntax", description: "Complex sentence transformations" },
  ],
  C2: [
    { title: "Idiomatic Expressions", description: "Native-level expressions and idioms" },
    { title: "Register and Style", description: "Adapting language to context" },
    { title: "Regional Variations", description: "Austrian, Swiss, and German differences" },
    { title: "Professional Language", description: "Business and academic German" },
  ],
};

/**
 * Grammar levels structure generated from default topics.
 *
 * Pre-computed array of GrammarLevel objects for all proficiency levels,
 * ready to use in the learning hub interface.
 */
export const grammarLevels: GrammarLevel[] = convertGrammarTopicsToLevels(defaultGrammarTopics);

/**
 * PDF books available for download.
 *
 * Contains metadata for downloadable PDF resources organized by level.
 * Books can be locked (requires level completion) or unlocked.
 */
export const pdfBooks: PDFBook[] = [
  {
    id: 'book1',
    title: 'German Grammar Essentials A1-A2',
    level: 'A1',
    fileSize: '2.5 MB',
    isLocked: false,
    downloadUrl: '/books/grammar-a1-a2.pdf',
  },
  {
    id: 'book2',
    title: 'Business German B2-C1',
    level: 'B2',
    fileSize: '5.2 MB',
    isLocked: true,
  },
];

